<template>
  <div class="llm-chat-window">
    <div class="llm-header">
      <h1 class="llm-title">{{ title }}</h1>
      <p class="llm-subtitle">{{ subtitle }}</p>
    </div>

    <div class="llm-messages" ref="messagesScroll" :style="{ maxHeight: messagesMaxHeightStyle, minHeight: '515px' }">
      <transition-group name="chat" tag="div" class="llm-entries">

        <div class="llm-entry" v-for="(entry, idx) in messages" :key="'entry-' + idx">
          <!-- Assistant message block -->
          <div class="llm-message-row" v-if="entry.assistant">
            <!-- <p class="llm-author-row left">{{ assistantName }}</p> -->
            <div class="llm-message-body">
              <div class="llm-avatar" :style="{ backgroundImage: 'url(' + assistantAvatar + ')' }" />
              <div class="llm-message-col">
                <div class="llm-bubble assistant-bubble" :class="{ 'error-bubble': entry.assistant.error }">
                  <div class="llm-bubble-content" v-html="renderMessageHtml(entry.assistant)"></div>
                  <div v-if="entry.assistant.error && entry.retryData" class="llm-error-actions">
                    <button 
                      class="llm-retry-btn" 
                      type="button"
                      @click="$emit('retry', idx)"
                      :disabled="sending"
                    >
                      <i class="fas fa-redo"></i> Retry
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- User message block -->
          <div class="llm-message-row" v-if="entry.user">
            <!-- <p class="llm-author-row right">{{ userName }}</p> -->
            <div class="llm-message-body is-user">
              <div class="llm-avatar" :style="{ backgroundImage: 'url(' + userAvatar + ')' }" />
              <div class="llm-message-col --user">
                <div class="llm-bubble user-bubble" v-html="renderMessageHtml(entry.user)" />
              </div>
            </div>
          </div>
        </div>

      </transition-group>

      <!-- Loading text row -->
      <div v-if="showLoadingRow">
        <div class="llm-message-row">
          <div class="llm-message-body">
            <div class="llm-avatar" :style="{ backgroundImage: 'url(' + assistantAvatar + ')' }" />
              <div class="llm-message-col">
                <div class="llm-bubble assistant-bubble">
                  <div class="llm-bubble-content" v-html="renderMessageHtml(currentLoadingMessage.assistant)"></div>
                </div>
              </div>
            </div>
          </div>
      </div>

      <slot name="addon"></slot>

    </div>

    <div class="llm-input-bar">
      <div class="llm-avatar small" :style="{ backgroundImage: 'url(' + userAvatar + ')' }" />
      <div class="llm-input-wrapper">
        <textarea
          ref="inputField"
          class="llm-input"
          :placeholder="placeholder"
          v-model="draft"
          @input="handleDraftInput"
          @keydown.enter.exact.prevent="handleSend"
          :disabled="disableChat || sending"
        />
        <div class="llm-input-actions">
          <button class="icon-btn" type="button" @click="$emit('attach')" :disabled="sending" title="Attach">
            <i class="fas fa-paperclip"></i>
          </button>
          <button class="icon-btn" type="button" @click="$emit('voice')" :disabled="sending" title="Voice">
            <i class="fas fa-microphone"></i>
          </button>
          <button v-if="showMapButton" class="icon-btn" type="button" @click="$emit('map')" :disabled="sending" title="Map">
            <i class="fas fa-map-marker-alt"></i>
          </button>
        </div>
      </div>
      <button class="llm-send" type="button" @click="handleSend" :disabled="disableChat || !canSend || sending">
        <span v-if="!sending">Send</span>
        <span v-else>
          <i class="fas fa-spinner fa-spin"></i>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import renderMarkdown, { sanitizeHtml as sanitizeHtmlUtil } from '@/utils/markdown';
export default {
  name: 'LLMChatWindow',
  props: {
    title: { type: String, default: 'Refine your campaign' },
    subtitle: { type: String, default: 'Use our AI assistant to refine your campaign data.' },
    assistantName: { type: String, default: 'Predict AI' },
    userName: { type: String, default: 'You' },
    assistantAvatar: { type: String, default: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCqIibm1Md9kGaLMm06Vz-jVRyu-BzZrX3ijJgJZEdVxaRErkA9lhuAFrj3jqARvpXakWzunDogN7cWhkFx4HpAZqEyhzSo3PzPbbBl2_-Ln0dpZ7n1DXqAWSkRlCy2vtrAd-FPz6ne9802Vy67TGALgtuXYEfU71qPSWwVC7cETUtxf_iwcqzi-ojVqmnraAlb48HnhrfCNj5ZCy86dxwJCWikfNU5fNBR1848X-45ExPv9Pa9Vd4Y_bC_U_frN42fyfODbNcvUdk' },
    userAvatar: { type: String, default: 'https://lh3.googleusercontent.com/aida-public/AB6AXuA8h3AGKVohkE9oU781aemyE4STwxhfx_3jej4CRAPMQBdUcgoR2rJsVbbLRwWfWQHFdPOaKyvOJcLervWX8hdgoOF-hF8AQPtMwCjV4uSGbblcXJ3YRmBkZr1qEYnm5k0mZ0PGDyRluolXfeEkQc_JR0ur1QbNx1dKULbLZpxusgvxzRlMAKn1WvEavdzaWK4-u5e2HikJTC8MUSlZS_Wa0hm6XeF2Zecxo41cCOb-0DscRtN3VeqVx6sl6zgTdNEntqRWtlX20Mo' },
    messages: {
      type: Array,
      // Each entry: { assistant: { text|html }, user: { text|html } }
      default: () => [
        {
          assistant: { text: "Hi there! I've generated a campaign based on your inputs. You can refine it further by asking me to make changes or provide more details. For example, you can ask me to adjust the target audience, modify the ad copy, or suggest different keywords. Let's make this campaign perfect for you!" },
          user: { text: 'Can you suggest some keywords for a campaign targeting young professionals interested in sustainable living?' }
        },
        {
          assistant: { html: '<span>Certainly! Here are some keywords for your campaign:</span><ul class="list"><li>eco-friendly products</li><li>green lifestyle</li><li>sustainable fashion</li><li>ethical consumption</li><li>zero waste</li></ul>' },
          user: null
        }
      ]
    },
    placeholder: { type: String, default: 'Type your message to Predict AI...' },
    sending: { type: Boolean, default: false },
    disableChat:{type:Boolean, default: false},
    showMapButton: { type: Boolean, default: false },
    messagesMaxHeight: { type: [Number, String], default: 500 },
    campaignCreateActiveStep: { required: true, type: [Number, String] }
  },
  data() {
    return {
      draft: '',
      campaignCreateActiveStepLocal: '',
      initialMessages: [
        {
          assistant: { text: "JARVIS, warm up the thrusters..." },
          user: null
        },
        {
          assistant: { text: "booting up the servers..." },
          user: null
        },
        {
          assistant: { text: "The architects are still drafting" },
          user: null
        },
        {
          assistant: { text: "Don't worry - a few bits tried to escape, but we caught them" },
          user: null
        },
        {
          assistant: { text: "The Sorting Hat is thinking..." },
          user: null
        },
        {
          assistant: { text: "Avengers, assemble the segments..." },
          user: null
        },
        {
          assistant: { text: "forging your marketing Infinity Stone..." },
          user: null
        },
        {
          assistant: { text: "Expecto patronum of personas..." },
          user: null
        },
        {
          assistant: { text: "rolling for charisma..." },
          user: null
        },
        {
          assistant: { text: "sending owls and to gather intel..." },
          user: null
        },
        {
          assistant: { text: "endgame loading, almost there. Patience is key" },
          user: null
        },
        {
          assistant: { text: "We're testing your patience..." },
          user: null
        },
      ],
      loadingMessageIndex: 0,
      loadingMessageTimer: null,
    };
  },
  computed: {
    canSend() {
      return this.draft.trim().length > 0;
    },
    messagesMaxHeightStyle() {
      const v = this.messagesMaxHeight;
      if (v === null || v === undefined || v === '') return '300px';
      return typeof v === 'number' ? `${v}px` : String(v);
    },
    currentLoadingMessage() {
      return this.initialMessages[this.loadingMessageIndex] || null;
    },
    showLoadingRow() {
      if (!this.sending || !this.currentLoadingMessage) return false;
      const len = Array.isArray(this.messages) ? this.messages.length : 0;
      if (len === 0) return true;
      const last = this.messages[len - 1];
      const hasAssistantContent = last && last.assistant && (last.assistant.text || last.assistant.html);
      const hasUserContent = last && last.user && (last.user.text || last.user.html);
      // Show loading once the user has sent something, even if the entry also contains assistant content.
      return hasUserContent || !hasAssistantContent;
    }
  },
  watch: {
    campaignCreateActiveStepLocal(newValue) {
      this.campaignCreateActiveStepLocal = ''; 
    },
    draft() {
      this.$nextTick(() => {
        this.resizeInput();
      });
    },
    sending(newValue) {
      if (newValue) {
        this.startLoadingMessages();
      } else {
        this.stopLoadingMessages();
      }
    }
  },
  methods: {
    handleDraftInput() {
      this.resizeInput();
    },
    resizeInput() {
      const el = this.$refs.inputField;
      if (!el) return;
      el.style.height = 'auto';
      const maxHeight = 60;
      const next = Math.min((el.scrollHeight - 20), maxHeight);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    },
    nameFor(role) {
      return role === 'user' ? this.userName : this.assistantName;
    },
    avatarFor(role) {
      return role === 'user' ? this.userAvatar : this.assistantAvatar;
    },
    handleSend() {
      if (!this.canSend || this.sending) return;
      const text = this.draft.trim();
      this.$emit('send', text);
      this.draft = '';
      this.$nextTick(this.scrollToBottom);
    },
    scrollToBottom() {
      const el = this.$refs.messagesScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    startLoadingMessages() {
      this.stopLoadingMessages();
      if (!this.initialMessages.length) return;

      this.loadingMessageIndex = 0;
      const advance = () => {
        if (!this.sending) return;
        this.loadingMessageTimer = setTimeout(() => {
          this.loadingMessageIndex = (this.loadingMessageIndex + 1) % this.initialMessages.length;
          this.scrollToBottom();
          advance();
        }, 2500);
      };
      advance();
    },
    stopLoadingMessages() {
      if (this.loadingMessageTimer) {
        clearTimeout(this.loadingMessageTimer);
        this.loadingMessageTimer = null;
      }
      this.loadingMessageIndex = 0;
    },
    renderMessageHtml(roleMessage) {
      if (!roleMessage) return '';
      if (roleMessage.html) return sanitizeHtmlUtil(roleMessage.html);
      if (roleMessage.text) {
        // If upstream text includes literal <br> tags, interpret them as line breaks
        const txt = String(roleMessage.text).replace(/<br\s*\/?>(\r\n?|\n)?/gi, '\n');
        return renderMarkdown(txt);
      }
      return '';
    }
  },
  mounted() {
    // this.scrollToBottom();
    this.campaignCreateActiveStepLocal = this.campaignCreateActiveStep;
    if (this.sending) {
      this.startLoadingMessages();
    }
  },
  updated() {
    this.scrollToBottom();
  },
  beforeDestroy() {
    this.stopLoadingMessages();
  }
};
</script>

<style scoped>
/* Container */
.llm-chat-window {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: #f9fafb;
  /* gray-50 */
  border: 1px solid #e5e7eb;
  /* gray-200 */
  border-radius: 8px;
  overflow: hidden;
}

/* Header */
.llm-header {
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.llm-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #111827 !important;
}

.llm-subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: #4b5563;
}

.llm-entries {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px
}

.llm-entry {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px
}

/* Messages area */
.llm-messages {
  width: 100%;
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 300px;
  /* Hide scrollbar (Webkit/Blink) */
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE 10+ */
}

.llm-messages::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.llm-message-row {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 8px 0px;
}

.llm-message-body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.llm-message-body.is-user {
  flex-direction: row-reverse;
}

.llm-avatar {
  width: 40px;
  height: 40px;
  border-radius: 9999px;
  background-size: cover;
  background-position: center;
  flex: 0 0 auto;
  border: 1px solid #e5e7eb;
  display: none;
}

.llm-avatar.small {
  width: 40px;
  height: 40px;
}

.llm-message-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.llm-message-col.--user {
  align-items: end;
}

.llm-author {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
}

.llm-author-row {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
}

.llm-author-row.left {
  text-align: left;
}

.llm-author-row.right {
  text-align: right;
}

.llm-bubble {
  max-width: min(65ch, 100%);
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #374151;
  line-height: 1.5;
}

.user-bubble {
  border-top-right-radius: 1px;
  border-top-left-radius: 8px;
}

.assistant-bubble {
  background: #fff;
  border-top-left-radius: 1px;
  border-top-right-radius: 8px;
}

.assistant-bubble {
  padding: 16px 18px;
  line-height: 1.6;
  border-radius: 10px;
  max-width: min(78ch, 100%);
}

.assistant-bubble.error-bubble {
  border-left: 3px solid #ef4444;
  background: #fef2f2;
}

.llm-error-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #fee2e2;
  display: flex;
  justify-content: flex-end;
}

.llm-retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #ef4444;
  border-radius: 6px;
  background: #fff;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.llm-retry-btn:hover:not(:disabled) {
  background: #ef4444;
  color: #fff;
}

.llm-retry-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Default assistant content color */
.assistant-bubble {
  color: #374151;
}

.assistant-bubble ::v-deep * {
  color: inherit;
}

/* Base type inside assistant content */
.assistant-bubble .llm-bubble-content {
  font-size: 14px;
  color: #111827 !important;
}

/* Basic list styling inside assistant HTML */
.llm-bubble .list {
  margin: 8px 0 0;
  padding-left: 18px;
}

.llm-bubble .list li {
  margin: 4px 0;
  line-height: normal !important;
}

/* Generic Markdown defaults within bubbles */
.llm-bubble ::v-deep p {
  margin: 6px 0;
}

.llm-bubble ::v-deep ul {
  margin: 8px 0 0;
  padding-left: 18px;
}

.llm-bubble ::v-deep ul li {
  margin: 4px 0;
  line-height: unset;
  display: block;
}

.llm-bubble ::v-deep ol {
  margin: 8px 0 0;
  padding-left: 22px;
  list-style: decimal;
}

.llm-bubble ::v-deep ol li {
  margin: 4px 0;
  line-height: unset;
    display: block;
}

.llm-bubble ::v-deep a {
  color: #1d4ed8;
  text-decoration: underline;
}

.llm-bubble ::v-deep a:hover {
  color: #1e40af;
}

.llm-bubble ::v-deep strong,
.llm-bubble ::v-deep b {
  font-weight: 700;
}

.llm-bubble ::v-deep em,
.llm-bubble ::v-deep i {
  font-style: italic;
}

.llm-bubble ::v-deep del {
  text-decoration: line-through;
}

.llm-bubble ::v-deep code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 0.95em;
  color: inherit;
  background: rgba(0, 0, 0, 0.04);
  padding: 2px 4px;
  border-radius: 4px;
}

.llm-bubble ::v-deep pre {
  margin: 8px 0 0;
  padding: 12px;
  background: rgba(2, 6, 23, 0.06);
  border-radius: 6px;
  overflow: auto;
}

.llm-bubble ::v-deep pre code {
  background: transparent;
  padding: 0;
  color: inherit;
  white-space: pre;
  display: block;
}

.llm-bubble ::v-deep blockquote {
  margin: 8px 0;
  padding-left: 12px;
  border-left: 3px solid #e5e7eb;
  color: #4b5563;
}

.llm-bubble ::v-deep hr {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 10px 0;
}

.llm-bubble ::v-deep img {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.llm-bubble ::v-deep table {
  width: 100%;
  border-collapse: collapse;
  margin: 8px 0;
}

.llm-bubble ::v-deep th,
.llm-bubble ::v-deep td {
  border: 1px solid #e5e7eb;
  padding: 6px 8px;
  text-align: left;
}

.llm-bubble ::v-deep thead th {
  background: #f3f4f6;
  font-weight: 600;
}

.user-bubble ::v-deep a {
  color: #93c5fd;
}

.user-bubble ::v-deep a:hover {
  color: #bfdbfe;
}

.user-bubble ::v-deep code {
  background: rgba(255, 255, 255, 0.12);
}

.user-bubble ::v-deep pre {
  background: rgba(255, 255, 255, 0.08);
}

.user-bubble ::v-deep blockquote {
  border-left-color: rgba(255, 255, 255, 0.2);
  color: #e5e7eb;
}

.user-bubble ::v-deep hr {
  border-top-color: rgba(255, 255, 255, 0.2);
}

.user-bubble ::v-deep th,
.user-bubble ::v-deep td {
  border-color: rgba(255, 255, 255, 0.15);
}

.user-bubble ::v-deep thead th {
  background: rgba(255, 255, 255, 0.08);
}

.llm-bubble ::v-deep h1,
.llm-bubble ::v-deep h2,
.llm-bubble ::v-deep h3,
.llm-bubble ::v-deep h4,
.llm-bubble ::v-deep h5,
.llm-bubble ::v-deep h6 {
  margin: 8px 0 6px;
  font-weight: 700;
  line-height: 1.25;
}

.llm-bubble ::v-deep h1 {
  font-size: 1.25rem;
}

.llm-bubble ::v-deep h2 {
  font-size: 1.125rem;
}

.llm-bubble ::v-deep h3 {
  font-size: 1rem;
}

.llm-bubble ::v-deep h4 {
  font-size: 0.9375rem;
  line-height: 1.25 !important;
  color: #111827 !important;
}

.llm-bubble ::v-deep h5 {
  font-size: 0.875rem;
}

.llm-bubble ::v-deep h6 {
  font-size: 0.8125rem;
}

/* Assistant typography overrides */
.assistant-bubble ::v-deep h1 {
  font-size: 1.125rem;
  margin: 2px 0 10px;
  font-weight: 800;
  line-height: 1.25 !important;
  color: #111827 !important;
}

.assistant-bubble ::v-deep h2 {
  font-size: 1rem;
  margin: 18px 0 10px;
  font-weight: 800;
  line-height: 1.3 !important;
  color: #111827 !important;
}

.assistant-bubble ::v-deep h3 {
  font-size: 0.9375rem;
  margin: 14px 0 8px;
  font-weight: 700;
  line-height: 1.35 !important;
  color: #374151 !important;
}

.assistant-bubble ::v-deep p {
  margin: 6px 0;
}

.assistant-bubble ::v-deep ul,
.assistant-bubble ::v-deep ol {
  margin: 8px 0 0;
  padding-left: 18px;
}

.assistant-bubble ::v-deep li {
  margin: 6px 0;
  line-height: 1.45 !important;
}

.assistant-bubble ::v-deep strong,
.assistant-bubble ::v-deep b {
  font-weight: 700;
  color: #111827 !important;
}

.assistant-bubble ::v-deep hr {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 14px 0;
}

/* Metric rows (nested list items) */
.assistant-bubble ::v-deep li ul {
  margin-top: 6px;
}

.assistant-bubble ::v-deep li ul li {
  margin: 4px 0;
  color: #4b5563 !important;
  font-size: 13px;
  line-height: 1.4 !important;
}

.assistant-bubble ::v-deep li ul li strong {
  color: #111827 !important;
}

/* Metric rows (class-based) */
.assistant-bubble ::v-deep .metric {
  margin: 2px 0;
  color: #4b5563 !important;
  font-size: 13px;
}

.assistant-bubble ::v-deep .metric strong {
  color: #111827 !important;
}


/* Typing indicator */
.typing-dots {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  background: currentColor;
  border-radius: 9999px;
  display: inline-block;
  opacity: 0.6;
  animation: typing-bounce 1.2s infinite ease-in-out;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.15s;
}

.typing-dots span:nth-child(3) { animation-delay: 0.3s; }

@keyframes typing-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
  40% { transform: translateY(-3px); opacity: 1; }
}

/* Subtle appear animation for chat entries */
.chat-enter-active { transition: all 200ms ease; }
.chat-enter { opacity: 0; transform: translateY(6px); }

/* Input bar */
.llm-input-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}
.llm-input-wrapper { position: relative; flex: 1; }
.llm-input {
  width: 100%; min-height: 40px; max-height: 60px; padding: 8px 112px 8px 12px; border-radius: 8px; border: 1px solid #d1d5db; background: #fff; color: #111827;
  font-size: 14px; resize: none; overflow-y: hidden;
}
.llm-input:focus { outline: none; box-shadow: 0 0 0 2px rgba(17,24,39,0.2); }
.llm-input-actions { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); display: flex; gap: 6px; }
.icon-btn { border: none; background: transparent; color: #6b7280; width: 32px; height: 32px; border-radius: 6px; }
.icon-btn:hover { color: #374151; background: #f3f4f6; }

.llm-send {
  min-width: 84px; height: 44px; padding: 0 16px; border: none; border-radius: 6px; background: #111827; color: #fff; font-weight: 700; cursor: pointer;
}
.llm-send:disabled { opacity: 0.6; cursor: not-allowed; }

/* Utility */
.text-right { text-align: right; }

@media (max-width: 480px) {
  .llm-header { padding: 16px; }
  .llm-messages { padding: 12px; }
  .llm-input-bar { padding: 10px; }
}


</style>
